// Main-thread client for the mockup engine. Each engine owns one worker, so
// disposing it releases all PSD memory. Falls back to running on the main
// thread when workers are unavailable.

let inlineCore = null;
const loadInline = () => (inlineCore ||= import("./core.js"));

export function createEngine() {
  let worker = null, nextId = 0, broken = false, disposed = false;
  const pending = new Map();
  // Document ids are namespaced per engine (inline documents share one module).
  const prefix = Math.random().toString(36).slice(2, 10);
  const opened = new Set();
  const ns = (docId) => {
    opened.add(docId);
    return `${prefix}:${docId}`;
  };

  try {
    worker = new Worker(new URL("./engine.worker.js", import.meta.url));
    worker.onmessage = (event) => {
      const { id, ok, result, error, progress, label } = event.data || {};
      const entry = pending.get(id);
      if (!entry) return;
      if (progress !== undefined && ok === undefined) {
        entry.onProgress?.(progress, label);
        return;
      }
      pending.delete(id);
      if (ok) entry.resolve(result);
      else entry.reject(new Error(error || "Mockup engine task failed"));
    };
    worker.onerror = (event) => {
      // Worker failed to load or crashed: retry outstanding work inline.
      event.preventDefault?.();
      broken = true;
      worker?.terminate();
      worker = null;
      for (const [id, entry] of pending) {
        pending.delete(id);
        runInline(entry.type, entry.payload, entry.onProgress).then(entry.resolve, entry.reject);
      }
    };
  } catch {
    worker = null;
    broken = true;
  }

  async function runInline(type, payload, onProgress) {
    const core = await loadInline();
    if (!core[type]) throw new Error(`Unknown engine task: ${type}`);
    return core[type](payload, onProgress || (() => {}));
  }

  function call(type, payload, { onProgress, transfer = [] } = {}) {
    if (disposed) return Promise.reject(new Error("Mockup engine closed"));
    if (!worker || broken) return runInline(type, payload, onProgress);
    return new Promise((resolve, reject) => {
      const id = ++nextId;
      pending.set(id, { resolve, reject, onProgress, type, payload });
      worker.postMessage({ id, type, payload }, transfer);
    });
  }

  return {
    /** source: { url } or { bytes: Uint8Array } */
    open: (docId, source, onProgress) =>
      call("open", { docId: ns(docId), source }, { onProgress, transfer: source.bytes && worker && !broken ? [source.bytes.buffer] : [] }),
    compile: (docId, maxSize, onProgress) => call("compile", { docId: ns(docId), maxSize }, { onProgress }),
    /** Stored templates: { previewUrl?, fullUrl?, psdUrl? } → { template } or { info }. */
    openTemplate: (docId, sources, onProgress) => call("openTemplate", { docId: ns(docId), ...sources }, { onProgress }),
    /** Template packages for a newly uploaded PSD: { preview, full, thumb, version, warnings }. */
    templatePackages: (docId, onProgress) => call("templatePackages", { docId: ns(docId) }, { onProgress }),
    /** art: { width, height, data } design-space RGBA; its buffer is transferred. */
    exportImage: (docId, art, appearance, options = {}, onProgress) =>
      call("exportImage", { docId: ns(docId), art, appearance, ...options }, { onProgress, transfer: worker && !broken ? [art.data.buffer] : [] }),
    prepareExport: (docId, onProgress) => call("prepareExport", { docId: ns(docId) }, { onProgress }),
    /** Preview composite, design layer pixels and the stored payload for a new listing. */
    uploadAssets: (docId, onProgress) => call("uploadAssets", { docId: ns(docId) }, { onProgress }),
    composite: (docId) => call("composite", { docId: ns(docId) }),
    designLayerPixels: (docId) => call("designLayerPixels", { docId: ns(docId) }),
    originalArtwork: (docId) => call("originalArtwork", { docId: ns(docId) }),
    close: (docId) => {
      opened.delete(docId);
      return call("close", { docId: `${prefix}:${docId}` });
    },
    get usesWorker() {
      return !!worker && !broken;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const entry of pending.values()) entry.reject(new Error("Mockup engine closed"));
      pending.clear();
      worker?.terminate();
      worker = null;
      // Inline documents live in the shared core module; drop ours.
      const ids = [...opened];
      inlineCore?.then((core) => ids.forEach((id) => core.close({ docId: `${prefix}:${id}` })));
    },
  };
}
